import type { AdmittedGameState, GameStateComparison } from './admission';
import type { GameStateConnectionStatus } from './transport';

export interface GameStateDiagnosticEvent {
  kind: 'record' | 'provider-reports' | 'connection';
  atMs: number;
  count: number;
}
export interface GameStateDiagnostics {
  invalidRecords: number;
  invalidReports: number;
  lastObservedAtMs: number | null;
  events: readonly GameStateDiagnosticEvent[];
}
export const emptyGameStateDiagnostics = (): GameStateDiagnostics => ({
  invalidRecords: 0, invalidReports: 0, lastObservedAtMs: null, events: [],
});

/** Bounded, tab-local evidence. No payload, URL, token, account or game identity
 * is retained. Repeated publications of the same admitted object count once.
 * Route teardown clears current counts but leaves history until auth changes.
 */
export class GameStateDiagnosticJournal {
  private seen = new WeakSet<object>();
  private events: GameStateDiagnosticEvent[] = [];
  private previousStatus: GameStateConnectionStatus = 'stopped';
  private observedAt: number | null = null;

  observe(status: GameStateConnectionStatus, records: ReadonlyMap<string, AdmittedGameState>,
    comparisons: ReadonlyMap<string, GameStateComparison>, atMs: number): GameStateDiagnostics {
    let invalidRecords = 0;
    let invalidReports = 0;
    let newRecords = 0;
    let newReports = 0;
    for (const row of records.values()) {
      if (row.withdrawal !== 'invalid-record') continue;
      invalidRecords++;
      if (!this.seen.has(row)) { this.seen.add(row); newRecords++; }
    }
    for (const row of comparisons.values()) {
      const count = row.invalidSources ?? 0;
      invalidReports += count;
      if (count > 0 && !this.seen.has(row)) { this.seen.add(row); newReports += count; }
    }
    if (newRecords) this.events.push({ kind: 'record', count: newRecords, atMs });
    if (newReports) this.events.push({ kind: 'provider-reports', count: newReports, atMs });
    if (status === 'disconnected' && this.previousStatus !== 'disconnected') {
      this.events.push({ kind: 'connection', count: 1, atMs });
    }
    this.previousStatus = status;
    this.events = this.events.slice(-20);
    if (status === 'live') this.observedAt = atMs;
    return { invalidRecords, invalidReports, lastObservedAtMs: this.observedAt, events: [...this.events] };
  }
}
